import { skipToken } from "@reduxjs/toolkit/query";
import { LoadingState, PosthogEventType } from "@repo/app-types/client/client";
import { TeammateLicenseType } from "@repo/app-types/entities/teammate";
import type { PredefinedTeammateRoleType } from "@repo/app-types/entities/teammatePermissions";
import {
  allTeammateRoleTypes,
  isPredefinedTeammateRoleType,
  predefinedRoleNameByType,
  TeammateRoleTypes,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { isPerformanceCompany } from "@repo/app-types/helpers/billingFeatureGating";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Input } from "@repo/ui/Inputs/input";
import { DescriptionSelect } from "@repo/ui/Select/descriptionSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconX } from "@tabler/icons-react";
import { isAxiosError } from "axios";
import { type FC, useRef, useState } from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useLicenseUtilizationQuery } from "../../../api/entities/company.js";
import { buildListTag, TagType } from "../../../api/helpers/tags.js";
import { api } from "../../../api/redux.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import { captureAnalyticsEvent } from "../../analytics/analyticsClient.js";
import { selectCompany, selectIsUsingSSO } from "../../company/companySlice.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import { buildInviteFailureMessage } from "./inviteFailures.js";
import {
  getLicenseTypeOptions,
  teammateLicenseCopy,
} from "./teammateLicenses.js";

const MAX_INVITE_EMAILS = 100;

const performanceRoleDescriptionByType: Readonly<
  Record<PredefinedTeammateRoleType, string>
> = {
  [TeammateRoleTypes.EDITOR]:
    "Build and edit playbooks. Manage settings, teammates, and all data.",
  [TeammateRoleTypes.REVIEWER]:
    "View dashboards, calls, and playbooks. No editing access.",
  [TeammateRoleTypes.TEAM_MEMBER]:
    "View calls and personal stats. No team stats or leaderboards.",
};

export const InviteTeammatesSection: FC = () => {
  const dispatch = useAppDispatch();

  const currentTeammate = useAppSelector(selectTeammate);
  const company = useAppSelector(selectCompany);
  const isUsingSSO = useAppSelector(selectIsUsingSSO);
  const isPerformance = company ? isPerformanceCompany(company) : false;

  const hasTeamPerformanceTab = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeamPerformanceTab,
  );

  // TODO: Better filter for not-allowed role selection
  const roleOptions = allTeammateRoleTypes
    .filter(isPredefinedTeammateRoleType)
    .filter((roleOption) => {
      switch (roleOption) {
        case TeammateRoleTypes.EDITOR:
          // Only allow editors to invite other editors
          return (
            currentTeammate?.permissions.role.type === TeammateRoleTypes.EDITOR
          );
        case TeammateRoleTypes.TEAM_MEMBER:
          // "team_member" role only available if team performance is enabled
          return hasTeamPerformanceTab;
        default:
          return true;
      }
    })
    .map((role) => ({
      label: predefinedRoleNameByType[role],
      value: role,
    }));

  const describedRoleOptions = roleOptions.map((option) => ({
    ...option,
    description: performanceRoleDescriptionByType[option.value],
  }));

  // The license picker only shows when the contract sells two or more
  // performance license tiers; invited teammates start on the selected tier.
  const { data: licenseUtilization } = useLicenseUtilizationQuery(
    isPerformance ? undefined : skipToken,
  );
  const licenseTypeOptions = getLicenseTypeOptions(
    licenseUtilization?.productUtilization ?? [],
  );
  const showLicensePicker = licenseTypeOptions.length >= 2;

  const [inviteEmails, setInviteEmails] = useState<string[]>(["", ""]);
  const [selectedRole, setSelectedRole] =
    useState<PredefinedTeammateRoleType | null>(null);
  const [selectedLicenseType, setSelectedLicenseType] =
    useState<TeammateLicenseType>(TeammateLicenseType.TEAMMATE);
  const [sendInvitationsLoadingState, setSendInvitationsLoadingState] =
    useState<LoadingState>(LoadingState.LOADED);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const onClickInvite = async () => {
    const emailsToInvite = inviteEmails.filter((email) => email.trim() !== "");

    if (emailsToInvite.length === 0 || selectedRole === null) return;

    setSendInvitationsLoadingState(LoadingState.LOADING);

    try {
      const axios = await getAxiosInstanceWithAuth();
      const invitesPayload = emailsToInvite.map((email) => ({
        email,
        permissions: {
          role: { type: selectedRole },
        },
        ...(showLicensePicker && { license_type: selectedLicenseType }),
      }));

      const { data } = await axios.post(`/company-invitations/invite`, {
        invites: invitesPayload,
      });

      const { company_invitations, invalid_emails } = data.data;
      // Absent while an older API version is still serving during a rollout.
      const skippedEmails: string[] = data.data.skipped_emails ?? [];
      const deactivatedEmails: string[] = data.data.deactivated_emails ?? [];
      const throttledEmails: string[] = data.data.throttled_emails ?? [];

      if (company_invitations.length > 0) {
        showToast({
          title: `${company_invitations.length} invite${
            company_invitations.length > 1 ? "s were" : " was"
          } successfully sent`,
          variant: ToastVariant.Success,
        });

        captureAnalyticsEvent(PosthogEventType.TeammatesInvite, {
          num_teammates_invited: company_invitations.length,
        });
      }

      if (invalid_emails.length > 0) {
        showToast({
          title: `${invalid_emails.length} invalid email${
            invalid_emails.length > 1 ? "s were" : " was"
          } skipped`,
          variant: ToastVariant.Error,
        });
      }

      if (skippedEmails.length > 0) {
        showToast({
          title:
            skippedEmails.length > 1
              ? `${skippedEmails.length} emails were skipped because they are already active teammates`
              : `1 email was skipped because it is already an active teammate`,
          variant: ToastVariant.Error,
        });
      }

      // An invitation never reactivates anybody, so these get told where the
      // action actually lives instead of a second invitation that would be
      // refused when accepted.
      if (deactivatedEmails.length > 0) {
        showToast({
          title:
            deactivatedEmails.length > 1
              ? `${deactivatedEmails.length} emails were skipped because they are deactivated teammates. Turn on “Show deactivated teammates” below to reactivate them.`
              : `1 email was skipped because it is a deactivated teammate. Turn on “Show deactivated teammates” below to reactivate them.`,
          variant: ToastVariant.Error,
        });
      }

      if (throttledEmails.length > 0) {
        showToast({
          title:
            throttledEmails.length > 1
              ? `${throttledEmails.length} emails were skipped because they have already been invited several times in the last 24 hours`
              : `1 email was skipped because it has already been invited several times in the last 24 hours`,
          variant: ToastVariant.Error,
        });
      }

      setInviteEmails(["", ""]);
      setSelectedRole(null);
      setSelectedLicenseType(TeammateLicenseType.TEAMMATE);

      // This posts outside RTK Query, so the tags the `inviteTeammates`
      // mutation would have invalidated are dispatched by hand. Both lists are
      // cached per search term (the roster per deactivated filter too), and
      // only invalidation evicts the entries nobody is subscribed to.
      dispatch(
        api.util.invalidateTags([
          buildListTag(TagType.CompanyInvitation, []),
          buildListTag(TagType.Teammate, []),
        ]),
      );

      setSendInvitationsLoadingState(LoadingState.LOADED);
    } catch (err) {
      // Handle rate limiting
      if (isAxiosError(err) && err.response?.status === 429) {
        showToast({
          title: buildInviteFailureMessage(err),
          variant: ToastVariant.Error,
        });
        setSendInvitationsLoadingState(LoadingState.LOADED);
        return;
      }

      if (
        isAxiosError(err) &&
        err.response?.status === 400 &&
        err.response?.data.code === "role_not_authorized"
      ) {
        showToast({
          title: err.response?.data.message,
          variant: ToastVariant.Error,
        });
        setSendInvitationsLoadingState(LoadingState.LOADED);
        return;
      }

      setSendInvitationsLoadingState(LoadingState.ERROR);
    }
  };

  const handleEmailChange =
    (index: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
      const newEmails = [...inviteEmails];
      newEmails[index] = evt.target.value;
      setInviteEmails(newEmails);
    };

  const handlePaste =
    (index: number) => (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedData = event.clipboardData.getData("Text");
      const emails = pastedData
        .split(/[\n,]+/)
        .map((email) => email.trim())
        .filter((email) => email !== "");

      // Calculate the number of elements to keep after the pasted data
      const remainingSpace = 100 - index;
      const numToKeep = remainingSpace - emails.length;

      // Check if the total number exceeds the limit
      if (index + emails.length > 100) {
        showToast({
          title: `You cannot invite more than 100 people at once.`,
          variant: ToastVariant.Error,
        });
        return;
      }

      setInviteEmails([
        ...inviteEmails.slice(0, index),
        ...emails,
        ...inviteEmails.slice(index + 1, index + 1 + numToKeep),
      ]);
    };

  const addInviteField = (index?: number) => {
    if (inviteEmails.length > MAX_INVITE_EMAILS) {
      return;
    }

    const newEmails = [...inviteEmails];
    const insertAtIndex =
      (index !== undefined ? index : inviteEmails.length) + 1;

    newEmails.splice(insertAtIndex, 0, "");
    setInviteEmails(newEmails);
    setTimeout(() => {
      inputRefs.current[insertAtIndex]?.focus();
    }, 0);
  };

  if (isUsingSSO) {
    return (
      <div className="pb-2 text-sm text-muted-foreground">
        Your organization is using SSO to manage access to Alpharun. Please add
        and remove teammates via your SSO provider.
      </div>
    );
  }

  return (
    <>
      <div className="pb-2 text-sm text-muted-foreground">
        {!isPerformance && "Your plan includes unlimited teammates. "}Add emails
        individually, or paste comma or newline separated emails.
      </div>
      {inviteEmails.map((email, index) => (
        <div
          className="group relative mb-2 flex max-w-md items-start gap-2"
          key={index}
        >
          <div className="relative w-[250px]">
            <Input
              key={index}
              placeholder="johndoe@example.com"
              value={email}
              onChange={handleEmailChange(index)}
              onPaste={handlePaste(index)}
              onKeyPress={(event) => {
                if (event.key === "Enter") {
                  addInviteField(index);
                }
              }}
              ref={(ref) => {
                inputRefs.current[index] = ref;
              }}
              maxLength={100}
            />
            <div className="absolute top-0 right-1 mt-1 hidden cursor-pointer text-muted-foreground group-hover:block">
              <IconButton
                variant={ButtonVariant.Secondary}
                icon={<IconX className="h-4 w-4" />}
                onClick={() => {
                  const newEmails = [...inviteEmails];
                  newEmails.splice(index, 1);
                  setInviteEmails(newEmails);
                }}
                isDisabled={inviteEmails.length === 1}
              />
            </div>
          </div>
        </div>
      ))}
      <Button
        variant={ButtonVariant.Secondary}
        onClick={() => addInviteField()}
        isDisabled={inviteEmails.length === MAX_INVITE_EMAILS}
      >
        Add email
      </Button>
      <div className="mt-4 flex items-end gap-4">
        <div>
          {isPerformance ? (
            <DescriptionSelect
              label="Role"
              placeholder="Select a role"
              popoverClassName="w-80 max-w-[calc(100vw-2rem)]"
              descriptionLineLimit={2}
              value={selectedRole}
              onChange={setSelectedRole}
              options={describedRoleOptions}
            />
          ) : (
            <SimpleSelect
              label="Role"
              placeholder="Select a role"
              value={selectedRole}
              onChange={setSelectedRole}
              options={roleOptions}
            />
          )}
        </div>
        {showLicensePicker ? (
          <div>
            <DescriptionSelect
              label="License"
              popoverClassName="w-80 max-w-[calc(100vw-2rem)]"
              descriptionLineLimit={1}
              value={selectedLicenseType}
              onChange={(value) => {
                setSelectedLicenseType(value);
              }}
              options={licenseTypeOptions.map((licenseType) => ({
                label: teammateLicenseCopy[licenseType].label,
                value: licenseType,
                description: teammateLicenseCopy[licenseType].capability,
              }))}
            />
          </div>
        ) : null}
        <div className="mt-5">
          <Button
            variant={ButtonVariant.Primary}
            onClick={onClickInvite}
            isLoading={sendInvitationsLoadingState === LoadingState.LOADING}
            isDisabled={
              selectedRole === null ||
              sendInvitationsLoadingState === LoadingState.LOADING
            }
          >
            Invite teammates
          </Button>
        </div>
      </div>
    </>
  );
};
